import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import RichTextEditor from '../../../components/RichTextEditor';

interface FeaturesSectionData {
  title: string;
  subtitle: string;
  enabled: boolean;
}

interface FeatureCard {
  id: string;
  title: string;
  description: string;
  iconName: string;
  iconColor: string;
  order: number;
  enabled: boolean;
}

const ICON_OPTIONS = [
  { name: 'shield-check', label: 'Shield Check' },
  { name: 'desktop', label: 'Desktop/Monitor' },
  { name: 'chart-bar', label: 'Chart Bar' },
  { name: 'clock', label: 'Clock' },
  { name: 'bell', label: 'Bell' },
  { name: 'lock', label: 'Lock' },
  { name: 'lightning-bolt', label: 'Lightning Bolt' },
  { name: 'shield', label: 'Shield' },
  { name: 'eye', label: 'Eye' },
  { name: 'sparkles', label: 'Sparkles' },
  { name: 'fire', label: 'Fire' },
  { name: 'star', label: 'Star' },
  { name: 'heart', label: 'Heart' },
  { name: 'check-circle', label: 'Check Circle' },
  { name: 'light-bulb', label: 'Light Bulb' },
];

const COLOR_OPTIONS = [
  { name: 'blue', value: 'bg-blue-100', iconColor: 'text-blue-600' },
  { name: 'indigo', value: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  { name: 'purple', value: 'bg-purple-100', iconColor: 'text-purple-600' },
  { name: 'pink', value: 'bg-pink-100', iconColor: 'text-pink-600' },
  { name: 'red', value: 'bg-red-100', iconColor: 'text-red-600' },
  { name: 'orange', value: 'bg-orange-100', iconColor: 'text-orange-600' },
  { name: 'yellow', value: 'bg-yellow-100', iconColor: 'text-yellow-600' },
  { name: 'green', value: 'bg-green-100', iconColor: 'text-green-600' },
  { name: 'teal', value: 'bg-teal-100', iconColor: 'text-teal-600' },
  { name: 'gray', value: 'bg-gray-100', iconColor: 'text-gray-600' },
];

const FeaturesEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sectionData, setSectionData] = useState<FeaturesSectionData>({
    title: '',
    subtitle: '',
    enabled: true,
  });
  const [features, setFeatures] = useState<FeatureCard[]>([]);
  const [editingFeature, setEditingFeature] = useState<FeatureCard | null>(
    null
  );
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load section data
      const sectionRef = doc(db, 'landingPageContent', 'featuresSection');
      const sectionSnap = await getDoc(sectionRef);
      if (sectionSnap.exists()) {
        setSectionData(sectionSnap.data() as FeaturesSectionData);
      }

      // Load features
      const featuresRef = collection(
        db,
        'landingPageContent',
        'featuresSection',
        'features'
      );
      const featuresSnap = await getDocs(featuresRef);
      const featuresData: FeatureCard[] = [];
      featuresSnap.forEach((doc) => {
        featuresData.push({ id: doc.id, ...doc.data() } as FeatureCard);
      });
      setFeatures(featuresData.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error loading features data:', error);
      showToast({
        message: 'Failed to load features data',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionSave = async () => {
    setSaving(true);
    try {
      const sectionRef = doc(db, 'landingPageContent', 'featuresSection');
      await setDoc(sectionRef, {
        ...sectionData,
        lastUpdated: serverTimestamp(),
      });

      showToast({
        message: 'Section header updated successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error saving section data:', error);
      showToast({
        message: 'Failed to save section header',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureSave = async (feature: FeatureCard) => {
    try {
      const featureRef = doc(
        db,
        'landingPageContent',
        'featuresSection',
        'features',
        feature.id || `feature-${Date.now()}`
      );
      await setDoc(featureRef, {
        title: feature.title,
        description: feature.description,
        iconName: feature.iconName,
        iconColor: feature.iconColor,
        order: feature.order,
        enabled: feature.enabled,
        lastUpdated: serverTimestamp(),
      });

      showToast({
        message: 'Feature card saved successfully',
        type: 'success',
      });

      setShowFeatureModal(false);
      setEditingFeature(null);
      loadData();
    } catch (error) {
      console.error('Error saving feature:', error);
      showToast({
        message: 'Failed to save feature card',
        type: 'error',
      });
    }
  };

  const handleFeatureDelete = async (featureId: string) => {
    if (!confirm('Are you sure you want to delete this feature?')) return;

    try {
      const featureRef = doc(
        db,
        'landingPageContent',
        'featuresSection',
        'features',
        featureId
      );
      await deleteDoc(featureRef);

      showToast({
        message: 'Feature deleted successfully',
        type: 'success',
      });

      loadData();
    } catch (error) {
      console.error('Error deleting feature:', error);
      showToast({
        message: 'Failed to delete feature',
        type: 'error',
      });
    }
  };

  const openAddFeatureModal = () => {
    setEditingFeature({
      id: '',
      title: '',
      description: '',
      iconName: 'shield-check',
      iconColor: 'blue',
      order: features.length + 1,
      enabled: true,
    });
    setShowFeatureModal(true);
  };

  const openEditFeatureModal = (feature: FeatureCard) => {
    setEditingFeature(feature);
    setShowFeatureModal(true);
  };

  // Helper to get icon path for rendering
  const getIconPath = (iconName: string) => {
    const icons: { [key: string]: JSX.Element } = {
      'shield-check': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
        />
      ),
      desktop: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      ),
      'chart-bar': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      ),
      clock: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      bell: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      ),
      lock: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      ),
      'lightning-bolt': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      ),
      shield: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      ),
      eye: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      ),
      sparkles: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
        />
      ),
      fire: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z"
        />
      ),
      star: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      ),
      heart: (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      ),
      'check-circle': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
      'light-bulb': (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      ),
    };
    return icons[iconName] || icons['shield-check'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/cms')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Features Section
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              "Why Choose Us?" section with feature cards
            </p>
          </div>
        </div>
      </div>

      {/* Section Header Card */}
      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <h2 className="text-lg font-semibold text-gray-900">Section Header</h2>

        {/* Rich Text for Section Title */}
        <RichTextEditor
          value={sectionData.title}
          onChange={(value) => setSectionData({ ...sectionData, title: value })}
          placeholder="e.g., Why Choose Us?"
          label="Section Title"
          required
        />

        {/* Rich Text for Subtitle */}
        <RichTextEditor
          value={sectionData.subtitle}
          onChange={(value) =>
            setSectionData({ ...sectionData, subtitle: value })
          }
          placeholder="e.g., Advanced AI detection technology..."
          label="Subtitle"
          required
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="section-enabled"
            checked={sectionData.enabled}
            onChange={(e) =>
              setSectionData({ ...sectionData, enabled: e.target.checked })
            }
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label
            htmlFor="section-enabled"
            className="ml-2 block text-sm text-gray-700"
          >
            Enable this section
          </label>
        </div>

        <button
          onClick={handleSectionSave}
          disabled={saving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Section Header'}
        </button>
      </div>

      {/* Feature Cards */}
      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Feature Cards</h2>
          <button
            onClick={openAddFeatureModal}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Feature</span>
          </button>
        </div>

        {features.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No features yet. Click "Add Feature" to create one.
          </p>
        ) : (
          <div className="space-y-4">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="border border-gray-200 rounded-lg p-4 flex items-start justify-between"
              >
                <div className="flex-1">
                  <div
                    className="font-semibold text-gray-900"
                    dangerouslySetInnerHTML={{ __html: feature.title }}
                  />
                  <div
                    className="text-sm text-gray-600 mt-1"
                    dangerouslySetInnerHTML={{ __html: feature.description }}
                  />
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <span>Icon: {feature.iconName}</span>
                    <span>Color: {feature.iconColor}</span>
                    <span>Order: {feature.order}</span>
                    <span
                      className={
                        feature.enabled ? 'text-green-600' : 'text-red-600'
                      }
                    >
                      {feature.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => openEditFeatureModal(feature)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleFeatureDelete(feature.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feature Modal */}
      {showFeatureModal && editingFeature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingFeature.id ? 'Edit Feature' : 'Add New Feature'}
            </h2>

            <div className="space-y-6">
              {/* Rich Text for Title */}
              <RichTextEditor
                value={editingFeature.title}
                onChange={(value) =>
                  setEditingFeature({ ...editingFeature, title: value })
                }
                placeholder="e.g., Real-time Protection When It Matters Most"
                label="Title"
                required
              />

              {/* Rich Text for Description */}
              <RichTextEditor
                value={editingFeature.description}
                onChange={(value) =>
                  setEditingFeature({ ...editingFeature, description: value })
                }
                placeholder="Describe this feature..."
                label="Description"
                required
              />

              {/* Visual Icon Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Icon
                </label>
                <div className="grid grid-cols-5 gap-3">
                  {ICON_OPTIONS.map((icon) => (
                    <button
                      key={icon.name}
                      type="button"
                      onClick={() =>
                        setEditingFeature({
                          ...editingFeature,
                          iconName: icon.name,
                        })
                      }
                      className={`p-3 border-2 rounded-lg flex flex-col items-center justify-center hover:border-primary transition ${
                        editingFeature.iconName === icon.name
                          ? 'border-primary bg-blue-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <svg
                        className="w-6 h-6 text-gray-600 mb-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        {getIconPath(icon.name)}
                      </svg>
                      <span className="text-[10px] text-gray-600 text-center leading-tight">
                        {icon.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Visual Color Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Icon Color
                </label>
                <div className="grid grid-cols-5 gap-3">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color.name}
                      type="button"
                      onClick={() =>
                        setEditingFeature({
                          ...editingFeature,
                          iconColor: color.name,
                        })
                      }
                      className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center hover:border-primary transition ${
                        editingFeature.iconColor === color.name
                          ? 'border-primary ring-2 ring-primary ring-offset-2'
                          : 'border-gray-200'
                      }`}
                    >
                      <div
                        className={`w-12 h-12 ${color.value} rounded-full flex items-center justify-center mb-2`}
                      >
                        <svg
                          className={`w-6 h-6 ${color.iconColor}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-600 capitalize">
                        {color.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={editingFeature.order}
                  onChange={(e) =>
                    setEditingFeature({
                      ...editingFeature,
                      order: parseInt(e.target.value),
                    })
                  }
                  min="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="feature-enabled"
                  checked={editingFeature.enabled}
                  onChange={(e) =>
                    setEditingFeature({
                      ...editingFeature,
                      enabled: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label
                  htmlFor="feature-enabled"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Enable this feature
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowFeatureModal(false);
                  setEditingFeature(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleFeatureSave(editingFeature)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Save Feature
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturesEditorPage;
